import { Suspense, lazy } from 'react'
import {
  createBrowserRouter,
  Outlet,
  useRouteError,
  useRoutes,
  Navigate,
} from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from 'config/user/store'
import { ErrorBoundary } from 'components/errorBoundary'
import { NoDataBgIcon, NoDataIcon } from 'components/icons'
import { Routes } from 'config/routesEndpoints'
import { useLocation } from 'react-router-dom'
import { LanguageWrapper } from './localeWrapper'

const AuthLayout = lazy(() => import('layout/AuthLayout'))
const MainLayout = lazy(() => import('layout/MainLayout'))

// Components for main layout
const Report = lazy(() => import('pages/publicPages/Report'))
const ThreeSixtyReport = lazy(
  () => import('pages/publicPages/ThreeSixtyReport')
)

export const Loader = () => <div>Loading...</div>
export const SuspenseOutlet = () => {
  return (
    <ErrorBoundary>
      <Suspense fallback={<Loader />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  )
}

export const Root = () => {
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const originParam = searchParams.get('origin') || ''
  const reportParam = searchParams.get('report') || ''

  if (originParam) {
    sessionStorage.setItem('origin', originParam)
  }
  let reportClass = ''
  if (reportParam === 'false') {
    reportClass = 'hide-report'
  }
  const className = `${originParam} ${reportClass}`.trim()
  return (
    <div id="detail" className={className}>
      <SuspenseOutlet />
    </div>
  )
}

const ErrorPage = () => {
  const error = useRouteError()
  const statusText = (error as { statusText?: string })?.statusText
  const message = (error as { message?: string })?.message

  return (
    <div id="error-page">
      <h1>Oops!</h1>
      <p>Sorry, an unexpected error has occurred.</p>
      <p>
        <i>{statusText || message}</i>
      </p>
    </div>
  )
}

export const NotFoundPage = () => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        width: '100%',
        height: '100%',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <NoDataIcon />
        <h2 style={{ paddingTop: 10 }}>404 Page Not Found</h2>
        <h3 style={{ fontWeight: 'normal', color: '#475467' }}>
          The page you are looking for does not exist.
        </h3>
      </div>
      <NoDataBgIcon />
    </div>
  )
}

export const RootPage = () => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        width: '100%',
        height: '100vh',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <img
          className="inspeqLogo"
          src="https://d26m1s0zd6rjfq.cloudfront.net/general/inspeq-assets/inspeqv2_logo_brand.png"
          alt="main-logo"
        />
      </div>
      <NoDataBgIcon />
    </div>
  )
}

const getRoutes = (isAuthenticated: boolean) => {
  const Layout = isAuthenticated ? MainLayout : AuthLayout
  return [
    { path: '', element: <RootPage /> },
    { path: Routes.report, element: <Report /> },
    {
      path: `${Routes.report}/${Routes.interactiveVideo}`,
      element: <ThreeSixtyReport />,
    },
    {
      path: `${Routes.report}/${Routes.annotationPicture}`,
      element: <ThreeSixtyReport />,
    },
    { path: 'report', element: <Navigate to="/" replace /> },
    { path: `${Routes.task}/*`, element: <RootPage /> },
    { path: '*', element: <NotFoundPage /> },
  ].map(route => ({
    ...route,
    element: <Layout>{route.element}</Layout>,
  }))
}

const AppRoutes = () => {
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated)
  const children = getRoutes(isAuthenticated)
  return useRoutes(children)
}

const router = createBrowserRouter([
  {
    path: '/:lang/*',
    element: <LanguageWrapper />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '*',
        element: <Root />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: '*',
            element: <AppRoutes />,
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/en/" replace />,
  },
])

export { router }
